import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Users, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}` } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 18, fontWeight: 700 },
  hdrT2: { fontSize: 12, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 },
  th: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'left', fontWeight: 700, fontSize: 11 },
  thC: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  thR: { borderBottom: '2px solid #d1d5db', padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: 11 },
  td: { borderBottom: '1px solid #e5e7eb', padding: '7px 10px', verticalAlign: 'middle', fontSize: 12 },
  tdC: { borderBottom: '1px solid #e5e7eb', padding: '7px 10px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  tdR: { borderBottom: '1px solid #e5e7eb', padding: '7px 10px', textAlign: 'right', verticalAlign: 'middle', fontSize: 12 },
  totRow: { fontWeight: 700, borderTop: '2px solid #1f2937', background: '#f3f4f6' },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

export default function RelacaoClientes({ tipo, titulo, tituloRelatorio }) {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const [config, setConfig] = useState({})
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    Promise.all([
      leiloesService.getByOrganizador(orgId),
      mapasService.getConfig(orgId).catch(() => ({}))
    ])
      .then(([leiloes, cfg]) => {
        const sorted = [...(leiloes || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
        setConfig(cfg)
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setDados(null); return }
    setLoading(true)
    const ordenacao = config.relacao_clientes_ordenacao || 'valor'
    const mostrarSexo = config.relacao_clientes_mostrar_sexo || false
    const mostrarQtdLotes = config.relacao_clientes_mostrar_qtd_lotes || false
    const ocultarValorLiquido = config.relacao_clientes_ocultar_valor_liquido || false
    const ocultarValorBruto = config.relacao_clientes_ocultar_valor_bruto || false
    const mostrarPctComissao = config.relacao_clientes_mostrar_pct_comissao || false
    mapasService.getRelacaoClientes(orgId, leilaoId, tipo, { ordenacao, mostrarSexo, mostrarQtdLotes, ocultarValorLiquido, ocultarValorBruto, mostrarPctComissao })
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId, tipo, config])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const ordenacao = config.relacao_clientes_ordenacao || 'valor'
      const mostrarSexo = config.relacao_clientes_mostrar_sexo || false
      const orientacao = config.relacao_clientes_orientacao_pdf || 'portrait'
      const mostrarQtdLotes = config.relacao_clientes_mostrar_qtd_lotes || false
      const ocultarValorLiquido = config.relacao_clientes_ocultar_valor_liquido || false
      const ocultarValorBruto = config.relacao_clientes_ocultar_valor_bruto || false
      const mostrarPctComissao = config.relacao_clientes_mostrar_pct_comissao || false
      const blob = await mapasService.getRelacaoClientesPdf(orgId, leilaoId, tipo, { ordenacao, mostrarSexo, orientacao, mostrarQtdLotes, ocultarValorLiquido, ocultarValorBruto, mostrarPctComissao })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const totalAnimais = dados?.clientes?.reduce((s, c) => s + c.qtd_animais, 0) || 0
  const totalLotes = dados?.clientes?.reduce((s, c) => s + (c.qtd_lotes || 0), 0) || 0
  const totalLiquido = dados?.clientes?.reduce((s, c) => s + c.valor_liquido, 0) || 0
  const totalBruto = dados?.clientes?.reduce((s, c) => s + c.valor_bruto, 0) || 0
  const totalComissaoRs = dados?.clientes?.reduce((s, c) => s + (c.comissao_rs || 0), 0) || 0
  const totalPctComissao = totalBruto ? (totalComissaoRs / totalBruto * 100) : 0

  const mostrarQtdLotes = config.relacao_clientes_mostrar_qtd_lotes || false
  const ocultarValorLiquido = config.relacao_clientes_ocultar_valor_liquido || false
  const ocultarValorBruto = config.relacao_clientes_ocultar_valor_bruto || false
  const mostrarPctComissao = config.relacao_clientes_mostrar_pct_comissao || false

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-gray-800">{titulo}</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null) }} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Selecione um leilão</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <div style={S.page}>
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>{logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}</div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>{tituloRelatorio}</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>LEILÃO: {leilaoAtual?.nome}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}><img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} /></div>
            </div>

            <table style={S.tbl}>
              <thead><tr>
                <th style={{ ...S.th, width: '30%' }}>{tipo === 'compradores' ? 'Comprador' : 'Vendedor'}</th>
                <th style={{ ...S.th, width: '22%' }}>Fazenda</th>
                {mostrarQtdLotes && <th style={{ ...S.thC, width: '8%' }}>Qtd. Lotes</th>}
                <th style={{ ...S.thC, width: '12%' }}>Qtd. Animais</th>
                {config.relacao_clientes_mostrar_sexo && <th style={{ ...S.thC, width: '8%' }}>Sexo</th>}
                {!ocultarValorLiquido && <th style={{ ...S.thR, width: '18%' }}>Valor Líquido</th>}
                {!ocultarValorBruto && <th style={{ ...S.thR, width: '18%' }}>Valor Bruto</th>}
                {mostrarPctComissao && <th style={{ ...S.thC, width: '10%' }}>% Média Comissão</th>}
              </tr></thead>
              <tbody>
                {dados.clientes.map((c, i) => (
                  <tr key={i}>
                    <td style={S.td}><strong>{c.nome}</strong></td>
                    <td style={S.td}>{c.fazenda || '—'}</td>
                    {mostrarQtdLotes && <td style={S.tdC}>{c.qtd_lotes || 0}</td>}
                    <td style={S.tdC}>{c.qtd_animais}</td>
                    {config.relacao_clientes_mostrar_sexo && <td style={S.tdC}>{(c.qtd_femea || 0)}F / {(c.qtd_macho || 0)}M</td>}
                    {!ocultarValorLiquido && <td style={S.tdR}>{brl(c.valor_liquido)}</td>}
                    {!ocultarValorBruto && <td style={S.tdR}>{brl(c.valor_bruto)}</td>}
                    {mostrarPctComissao && <td style={S.tdC}>{(c.pct_medio_comissao || 0).toFixed(2).replace('.', ',')}%</td>}
                  </tr>
                ))}
                <tr style={S.totRow}>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}><strong>TOTAL</strong></td>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}></td>
                  {mostrarQtdLotes && <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalLotes}</strong></td>}
                  <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalAnimais}</strong></td>
                  {config.relacao_clientes_mostrar_sexo && <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}></td>}
                  {!ocultarValorLiquido && <td style={{ ...S.tdR, borderTop: '2px solid #1f2937' }}><strong>{brl(totalLiquido)}</strong></td>}
                  {!ocultarValorBruto && <td style={{ ...S.tdR, borderTop: '2px solid #1f2937' }}><strong>{brl(totalBruto)}</strong></td>}
                  {mostrarPctComissao && <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalPctComissao.toFixed(2).replace('.', ',')}%</strong></td>}
                </tr>
              </tbody>
            </table>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">{titulo} — {leilaoAtual?.nome}</span>
              <div className="flex items-center gap-2">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title={titulo} style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
